import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import LanguageToggle from "@/components/LanguageToggle";
import ThemeToggle from "@/components/ThemeToggle";
import Logo from "@/components/Logo";
import { useTheme } from "@/lib/theme";

/**
 * Solid, responsive site header shared across every route except Landing's
 * bespoke hero. Hamburger drawer on mobile, full nav on desktop.
 * Nordic MarTech palette: cream (#FFFDF6) light, near-black (#0A0A0A) dark,
 * amber (#FFC53D) as the single brand accent.
 */

const NAV = [
  { label: "How it works", to: "/how-it-works", testid: "header-how-link", breakpoint: "md" },
  { label: "Benchmarks", to: "/benchmarks", testid: "header-benchmarks-link", breakpoint: "md" },
  { label: "For CMOs", to: "/for-cmos", testid: "header-for-cmos-link", breakpoint: "lg" },
  { label: "Pricing", to: "/pricing", testid: "header-pricing-link", breakpoint: "md" },
];

function BrandMark({ dark }) {
  return (
    <Link
      to="/"
      data-testid="site-brand"
      className="flex items-center gap-3 group"
    >
      <div className="transition-transform group-hover:-rotate-3">
        <Logo size="md" inverse={dark} />
      </div>
      <span className={`text-xs uppercase tracking-[0.28em] font-semibold ${dark ? "text-slate-100" : "text-slate-900"}`}>
        PressImpact
      </span>
    </Link>
  );
}

export default function SiteHeader({ cta = { label: "Open calculator", to: "/calculator" } }) {
  const { theme } = useTheme();
  const dark = theme === "dark";
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Close drawer on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);
  // Lock body scroll while drawer is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const headerBg = dark
    ? "bg-[#0A0A0A]/90 backdrop-blur border-white/10"
    : "bg-[#FFFDF6]/90 backdrop-blur border-slate-200";
  const linkColor = dark ? "text-slate-300 hover:text-white" : "text-slate-600 hover:text-slate-900";
  const activeColor = dark ? "text-white" : "text-slate-900";

  const isActive = (to) => location.pathname === to || location.pathname.startsWith(to + "/");

  return (
    <>
      <header className={`border-b ${headerBg} sticky top-0 z-40`}>
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 py-4 flex items-center justify-between">
          <BrandMark dark={dark} />

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-6 lg:gap-8" aria-label="Primary">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={`text-[11px] uppercase tracking-[0.24em] font-semibold transition-colors ${
                  item.breakpoint === "lg" ? "hidden lg:inline" : "inline"
                } ${isActive(item.to) ? activeColor : linkColor}`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <ThemeToggle />
            <div className="hidden sm:block">
              <LanguageToggle />
            </div>
            {cta && (
              <Link to={cta.to} className="hidden md:block">
                <Button
                  data-testid="header-cta"
                  size="sm"
                  className={`rounded-sm px-4 py-4 text-xs uppercase tracking-[0.18em] font-semibold ${
                    dark
                      ? "bg-[#FFC53D] hover:bg-[#FFD866] text-slate-900"
                      : "bg-slate-900 hover:bg-slate-800 text-white shadow-[3px_3px_0_0_#FFC53D] hover:shadow-[2px_2px_0_0_#FFC53D] hover:translate-x-[1px] hover:translate-y-[1px] transition-all"
                  }`}
                >
                  {cta.label}
                  <ArrowUpRight className="h-4 w-4 ml-1.5" />
                </Button>
              </Link>
            )}
            {/* Hamburger */}
            <button
              data-testid="header-menu-toggle"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              className={`md:hidden h-9 w-9 grid place-items-center rounded-sm border transition-colors ${
                dark
                  ? "border-white/20 bg-white/5 text-slate-100 hover:bg-white/10"
                  : "border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
              }`}
            >
              {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {open && (
        <div
          data-testid="mobile-menu"
          className={`fixed inset-0 top-[65px] z-30 md:hidden ${dark ? "bg-[#0A0A0A]" : "bg-[#FFFDF6]"}`}
        >
          <div className="px-5 py-8 flex flex-col gap-1 max-w-md mx-auto">
            {NAV.map((item, idx) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`mobile-${item.testid}`}
                className={`flex items-center justify-between py-5 border-b ${
                  dark ? "border-white/10" : "border-slate-200"
                } group`}
              >
                <span className={`font-heading text-2xl font-light tracking-tight ${
                  isActive(item.to)
                    ? dark ? "text-[#FFC53D]" : "text-slate-900"
                    : dark ? "text-slate-100" : "text-slate-900"
                }`}>
                  {item.label}
                </span>
                <span className={`text-[10px] mono tracking-[0.24em] ${dark ? "text-slate-500" : "text-slate-400"}`}>
                  0{idx + 1}
                </span>
              </Link>
            ))}

            {cta && (
              <Link to={cta.to} className="mt-8">
                <Button
                  data-testid="mobile-header-cta"
                  size="lg"
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm px-6 py-6 text-sm uppercase tracking-[0.18em] font-semibold shadow-[4px_4px_0_0_#FFC53D]"
                >
                  {cta.label}
                  <ArrowUpRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            )}

            <div className="mt-6 flex items-center justify-between">
              <LanguageToggle testPrefix="mobile-" />
              <span className={`text-[10px] tracking-[0.28em] uppercase font-semibold ${dark ? "text-slate-500" : "text-slate-400"}`}>
                Kaada Nordic MarTech · v1
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
